import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  getActiveTenantCount,
  getActiveSubscriptionCount,
  getMonthlyRevenue,
  getPendingPaymentCount,
} from '../services/tenantService';
import { Building2, CreditCard, TrendingUp, AlertCircle } from 'lucide-react';

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState({
    totalRestaurants: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    pendingPayments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const [restaurants, subscriptions, revenue, payments] = await Promise.all([
          getActiveTenantCount(),
          getActiveSubscriptionCount(),
          getMonthlyRevenue(),
          getPendingPaymentCount(),
        ]);

        setStats({
          totalRestaurants: restaurants,
          activeSubscriptions: subscriptions,
          monthlyRevenue: revenue,
          pendingPayments: payments,
        });
      } catch (err: any) {
        console.error('Error loading stats:', err);
        setError(err.message || 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const StatCard = ({
    icon,
    label,
    value,
    color,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    color: string;
  }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`${color} p-3 rounded-lg`}>{icon}</div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">Welcome to FoodHub Admin Dashboard</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<Building2 className="w-6 h-6 text-blue-600" />}
            label="Total Restaurants"
            value={loading ? '...' : stats.totalRestaurants}
            color="bg-blue-100"
          />
          <StatCard
            icon={<CreditCard className="w-6 h-6 text-green-600" />}
            label="Active Subscriptions"
            value={loading ? '...' : stats.activeSubscriptions}
            color="bg-green-100"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
            label="Monthly Revenue"
            value={loading ? '...' : `$${stats.monthlyRevenue.toLocaleString()}`}
            color="bg-purple-100"
          />
          <StatCard
            icon={<AlertCircle className="w-6 h-6 text-orange-600" />}
            label="Pending Payments"
            value={loading ? '...' : stats.pendingPayments}
            color="bg-orange-100"
          />
        </div>

        {/* Navigation Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/tenants"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Manage Restaurants</h2>
                <p className="text-gray-600 mt-2">Add, edit, and manage restaurant tenants</p>
              </div>
              <Building2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
            </div>
          </Link>

          <Link
            to="/billing"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Billing & Payments</h2>
                <p className="text-gray-600 mt-2">Manage subscriptions and process payments</p>
              </div>
              <CreditCard className="w-6 h-6 text-green-600 flex-shrink-0" />
            </div>
          </Link>

          <Link
            to="/analytics"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Analytics</h2>
                <p className="text-gray-600 mt-2">View cross-tenant analytics and reports</p>
              </div>
              <TrendingUp className="w-6 h-6 text-purple-600 flex-shrink-0" />
            </div>
          </Link>

          <Link
            to="/settings"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Settings</h2>
                <p className="text-gray-600 mt-2">Configure pricing plans and system settings</p>
              </div>
              <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
            </div>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;

